import React, { useState, useRef, useMemo } from 'react';
import { Badge, emptyMsg, cardWrap, neuShadow, neuShadowSm, inp } from './crmShared';
import { ChevronRight, Search, X } from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';

const STATUS_ORDER = ['pending', 'in_progress', 'completed', 'declined'];
const STATUS_COLORS = {
  pending: '#F26522',
  in_progress: '#2E8B98',
  completed: '#62BB46',
  declined: '#DC3545'
};

export default function ReferralsTab({ referrals, onContactClick }) {
  const containerRef = useRef(null);
  const { onKeyDown: handleKeyDown } = useArrowNav(containerRef, '[data-referral-item]', { horizontal: false, vertical: true });

  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  // Filter referrals based on search
  const filteredReferrals = useMemo(() => {
    if (!searchTerm.trim()) return referrals;

    const term = searchTerm.toLowerCase();
    return referrals.filter(r => {
      const matches = [
        r.referral_reason?.toLowerCase().includes(term),
        r.patient_first?.toLowerCase().includes(term),
        r.patient_last?.toLowerCase().includes(term),
        r.ref_contact_first?.toLowerCase().includes(term),
        r.ref_contact_last?.toLowerCase().includes(term),
        r.to_contact_first?.toLowerCase().includes(term),
        r.to_contact_last?.toLowerCase().includes(term),
        r.specialty?.toLowerCase().includes(term)
      ];
      return matches.some(m => m);
    });
  }, [referrals, searchTerm]);

  // Group referrals by status
  const groupedReferrals = useMemo(() => {
    const groups = {};

    // Initialize groups in order
    STATUS_ORDER.forEach(status => {
      groups[status] = [];
    });

    // Add filtered referrals to their status groups
    filteredReferrals.forEach(r => {
      const status = r.status || 'pending';
      if (!groups[status]) groups[status] = [];
      groups[status].push(r);
    });

    return groups;
  }, [filteredReferrals]);

  // Auto-expand all groups when search is active
  const isSearchActive = searchTerm.trim().length > 0;
  const activeExpandedGroups = isSearchActive
    ? new Set(STATUS_ORDER.filter(status => groupedReferrals[status].length > 0))
    : expandedGroups;

  const toggleGroup = (status) => {
    if (isSearchActive) return; // Don't allow manual toggle during search
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(status)) {
      newExpanded.delete(status);
    } else {
      newExpanded.add(status);
    }
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    setExpandedGroups(new Set(STATUS_ORDER.filter(status => groupedReferrals[status].length > 0)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Count total referrals being displayed
  const totalCount = Object.values(groupedReferrals).reduce((sum, group) => sum + group.length, 0);
  const hasResults = totalCount > 0;

  const searchInputStyle = {
    ...inp,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    marginBottom: '16px',
    borderRadius: '6px',
    border: '1px solid #DEE2E6',
    backgroundColor: '#FFFFFF',
    boxShadow: neuShadowSm
  };

  const clearButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    color: '#6C757D',
    transition: 'color 0.2s',
    padding: 0,
    border: 'none',
    background: 'none'
  };

  const controlsStyle = {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px'
  };

  const buttonStyle = {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 600,
    border: '1px solid #DEE2E6',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
    color: '#212529',
    transition: 'all 0.2s',
    boxShadow: neuShadowSm
  };

  const groupHeaderStyle = (status, isExpanded) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    cursor: 'pointer',
    backgroundColor: '#F8F9FA',
    borderBottom: '1px solid #E9ECEF',
    transition: 'background-color 0.2s',
    userSelect: 'none',
    ':hover': {
      backgroundColor: '#F1F3F5'
    }
  });

  const chevronStyle = (isExpanded) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    transition: 'transform 0.3s ease',
    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
    color: '#6C757D',
    flexShrink: 0
  });

  const groupContentStyle = (isExpanded, count) => ({
    maxHeight: isExpanded ? `${count * 120}px` : '0px',
    overflow: 'hidden',
    transition: 'max-height 0.3s ease',
    backgroundColor: '#FFFFFF'
  });

  const referralItemStyle = (index) => ({
    padding: '12px 16px',
    borderBottom: '1px solid #E9ECEF',
    backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8F9FA',
    transition: 'background-color 0.2s',
    outline: 'none',
    cursor: 'pointer'
  });

  const referralItemFocusStyle = {
    backgroundColor: '#DCE2E8'
  };

  const statusLabelStyle = (status) => ({
    fontSize: '12px',
    fontWeight: 600,
    color: '#212529',
    textTransform: 'capitalize',
    flex: 1
  });

  const countBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    height: '24px',
    padding: '0 6px',
    borderRadius: '12px',
    backgroundColor: '#E9ECEF',
    fontSize: '11px',
    fontWeight: 600,
    color: '#495057'
  };

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown} style={cardWrap}>
      {/* Search Bar */}
      <div style={searchInputStyle}>
        <Search size={16} color="#6C757D" />
        <input
          type="text"
          placeholder="Search by reason, patient, contact, or specialty..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
            fontSize: '13px',
            color: '#212529'
          }}
        />
        {searchTerm && (
          <button
            onClick={clearSearch}
            onMouseEnter={(e) => e.target.style.color = '#212529'}
            onMouseLeave={(e) => e.target.style.color = '#6C757D'}
            style={clearButtonStyle}
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Expand/Collapse Controls */}
      {hasResults && (
        <div style={controlsStyle}>
          <button
            onClick={expandAll}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#E9ECEF'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
            style={buttonStyle}
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#E9ECEF'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
            style={buttonStyle}
          >
            Collapse All
          </button>
        </div>
      )}

      {/* Empty State */}
      {!hasResults && (
        <div style={emptyMsg}>
          {searchTerm ? 'No referrals match your search' : 'No referrals found'}
        </div>
      )}

      {/* Accordion Groups */}
      {hasResults && STATUS_ORDER.map(status => {
        const items = groupedReferrals[status];
        if (items.length === 0) return null;

        const isExpanded = activeExpandedGroups.has(status);
        const statusColor = STATUS_COLORS[status] || '#6C757D';

        return (
          <div key={status} style={{ marginBottom: '8px', borderRadius: '6px', overflow: 'hidden', boxShadow: neuShadow }}>
            {/* Group Header */}
            <div
              onClick={() => toggleGroup(status)}
              onMouseEnter={(e) => !isSearchActive && (e.currentTarget.style.backgroundColor = '#F1F3F5')}
              onMouseLeave={(e) => !isSearchActive && (e.currentTarget.style.backgroundColor = '#F8F9FA')}
              style={{
                ...groupHeaderStyle(status, isExpanded),
                cursor: isSearchActive ? 'default' : 'pointer'
              }}
            >
              <div style={chevronStyle(isExpanded)}>
                <ChevronRight size={18} color={statusColor} strokeWidth={2.5} />
              </div>

              <Badge
                text={status.replace(/_/g, ' ')}
                color={statusColor}
              />

              <div style={statusLabelStyle(status)}>
                {status.replace(/_/g, ' ')}
              </div>

              <div style={countBadgeStyle}>
                {items.length}
              </div>
            </div>

            {/* Group Content */}
            <div style={groupContentStyle(isExpanded, items.length)}>
              {items.map((r, index) => (
                <div
                  key={r.id}
                  data-referral-item
                  tabIndex={0}
                  style={referralItemStyle(index)}
                  onFocus={(e) => {
                    e.currentTarget.style.backgroundColor = '#DCE2E8';
                  }}
                  onBlur={(e) => {
                    const bgColor = index % 2 === 0 ? '#FFFFFF' : '#F8F9FA';
                    e.currentTarget.style.backgroundColor = bgColor;
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Badge
                        text={r.direction === 'incoming' ? 'Incoming' : 'Outgoing'}
                        color={r.direction === 'incoming' ? '#62BB46' : '#2E8B98'}
                      />
                      {r.specialty && (
                        <span style={{ fontSize: 11, color: '#ADB5BD' }}>
                          {r.specialty}
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: '#ADB5BD', whiteSpace: 'nowrap', marginLeft: '8px' }}>
                      {r.referral_date}
                    </span>
                  </div>

                  <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 4 }}>
                    {r.referral_reason}
                  </div>

                  <div style={{ fontSize: 12, color: '#6C757D' }}>
                    {r.patient_first && (
                      <span>
                        Patient: {r.patient_id ? (
                          <span onClick={() => onContactClick && onContactClick(r.patient_id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>{r.patient_first} {r.patient_last}</span>
                        ) : (
                          <span>{r.patient_first} {r.patient_last}</span>
                        )}{' '}
                      </span>
                    )}
                    {r.ref_contact_first && r.referring_contact_id && (
                      <span>
                        From: <span onClick={() => onContactClick && onContactClick(r.referring_contact_id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>{r.ref_contact_first} {r.ref_contact_last}</span>{' '}
                      </span>
                    )}
                    {r.ref_contact_first && !r.referring_contact_id && (
                      <span>From: {r.ref_contact_first} {r.ref_contact_last}{' '}</span>
                    )}
                    {r.to_contact_first && r.referred_to_contact_id && (
                      <span>
                        To: <span onClick={() => onContactClick && onContactClick(r.referred_to_contact_id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>{r.to_contact_first} {r.to_contact_last}</span>
                      </span>
                    )}
                    {r.to_contact_first && !r.referred_to_contact_id && (
                      <span>To: {r.to_contact_first} {r.to_contact_last}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
